import React, { useMemo, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { useUserConnections } from "@/api/accounts";
import PremiumBadge from "@/components/premium-badge";
import ScrollableList from "@/components/scrollable-list";
import { HStack, Modal, Stack, Tabs, Text } from "@/components/ui";
import VerificationBadge from "@/components/verification-badge";
import AccountContainer from "@/containers/account-container";
import { useAuth } from "@/contexts/auth/useAuth";
import PlaceholderAccount from "@/features/placeholder/components/placeholder-account";

import type { Account } from "@/schemas";

const messages = defineMessages({
  mutuals: {
    id: "modals.user-connections.tabs.mutuals",
    defaultMessage: "Mutuals",
  },
  verifiedFollowers: {
    id: "modals.user-connections.tabs.verified-followers",
    defaultMessage: "Verified Followers",
  },
  followers: {
    id: "modals.user-connections.tabs.followers",
    defaultMessage: "Followers",
  },
  following: {
    id: "modals.user-connections.tabs.following",
    defaultMessage: "Following",
  },
  followingHelpText: {
    id: "modals.user-connections.tabs.following-help-text",
    defaultMessage: "Followed Users",
  },
  limitedView: {
    id: "modals.user-connections.limited-text",
    defaultMessage: "Only @{username} can see the full list of {tab}.",
  },
});

enum UserConnectionsTabs {
  MUTUALS = "mutuals",
  VERIFIED_FOLLOWERS = "verified-followers",
  FOLLOWERS = "followers",
  FOLLOWING = "following",
}

interface UserConnectionsModalProps {
  account: Account;
  initialTab: UserConnectionsTabs;
  onClose?(): void;
}

const UserConnectionsModal: React.FC<UserConnectionsModalProps> = (props) => {
  const { account, initialTab, onClose } = props;

  const intl = useIntl();
  const { currentAccount } = useAuth();

  const [activeTab, setActiveTab] = useState<UserConnectionsTabs>(initialTab);

  let endpoint: "followers" | "following" = "followers";
  if (activeTab === UserConnectionsTabs.FOLLOWING) {
    endpoint = "following";
  }

  const { accounts, isLoading, hasNextPage, fetchNextPage } =
    useUserConnections(account.id, {
      endpoint,
      mutual: activeTab === UserConnectionsTabs.MUTUALS,
      verified: activeTab === UserConnectionsTabs.VERIFIED_FOLLOWERS,
    });

  const isCurrentUser = account.id === currentAccount?.id;
  const shouldAppendLimitedViewText =
    !isCurrentUser &&
    !hasNextPage &&
    !isLoading &&
    activeTab !== UserConnectionsTabs.MUTUALS &&
    accounts.length > 0;
  const placeholderCount = 6;
  const itemHeight = 54;
  const lastItemHeight = 66;
  const numOfItems = isLoading ? placeholderCount : accounts.length - 1;
  const modalHeight =
    numOfItems >= 8
      ? 400
      : itemHeight * numOfItems +
        lastItemHeight +
        (shouldAppendLimitedViewText ? 60 : 0);

  function renderAppendedText() {
    switch (activeTab) {
      case UserConnectionsTabs.VERIFIED_FOLLOWERS:
        return intl.formatMessage(messages.verifiedFollowers).toLowerCase();
      case UserConnectionsTabs.FOLLOWERS:
        return intl.formatMessage(messages.followers).toLowerCase();
      case UserConnectionsTabs.FOLLOWING:
        return intl.formatMessage(messages.followingHelpText).toLowerCase();
      default:
        return intl.formatMessage(messages.mutuals).toLowerCase();
    }
  }

  const tabItems = useMemo(() => {
    const items = [
      {
        label: intl.formatMessage(messages.verifiedFollowers),
        action: () => setActiveTab(UserConnectionsTabs.VERIFIED_FOLLOWERS),
        name: UserConnectionsTabs.VERIFIED_FOLLOWERS,
      },
      {
        label: intl.formatMessage(messages.followers),
        action: () => setActiveTab(UserConnectionsTabs.FOLLOWERS),
        name: UserConnectionsTabs.FOLLOWERS,
      },
      {
        label: intl.formatMessage(messages.following),
        action: () => setActiveTab(UserConnectionsTabs.FOLLOWING),
        name: UserConnectionsTabs.FOLLOWING,
      },
    ];

    if (!isCurrentUser) {
      items.unshift({
        label: intl.formatMessage(messages.mutuals),
        action: () => setActiveTab(UserConnectionsTabs.MUTUALS),
        name: UserConnectionsTabs.MUTUALS,
      });
    }

    return items;
  }, [isCurrentUser]);

  return (
    <Modal
      title={
        <Stack>
          <HStack alignItems="center" space={1}>
            <Text
              weight="bold"
              dangerouslySetInnerHTML={{ __html: account.display_name_html }}
              truncate
              size="xl"
              className="max-w-[95%]"
            />

            {account.verified && <VerificationBadge />}
            {account.premium && <PremiumBadge />}
          </HStack>

          <Text theme="muted" size="sm" truncate>
            @{account.acct}
          </Text>
        </Stack>
      }
      onClose={onClose}
      bodyClassName={"p-0!"}
    >
      <Stack space={4}>
        <div className="px-6">
          <Tabs items={tabItems} activeItem={activeTab} />
        </div>

        <ScrollableList
          scrollKey="mutual-followers-list"
          emptyMessage={
            <FormattedMessage
              id="modals.user-connections.connections.none"
              defaultMessage="There are no {type} to show."
              values={{
                type: renderAppendedText(),
              }}
            />
          }
          className="max-w-full"
          style={{
            height: modalHeight,
            minHeight: 100,
          }}
          itemClassName="px-6 pb-3 last:pb-6"
          useWindowScroll={false}
          hasMore={hasNextPage}
          onLoadMore={fetchNextPage}
          isLoading={isLoading}
          placeholderComponent={PlaceholderAccount}
          placeholderCount={placeholderCount}
          append={
            shouldAppendLimitedViewText && (
              <Text align="center" theme="muted" size="sm" className="pb-8">
                {intl.formatMessage(messages.limitedView, {
                  username: account.acct,
                  tab: renderAppendedText(),
                })}
              </Text>
            )
          }
        >
          {accounts.map((account) => (
            <AccountContainer
              key={account.id}
              id={account.id}
              showProfileHoverCard={false}
            />
          ))}
        </ScrollableList>
      </Stack>
    </Modal>
  );
};

export {
  UserConnectionsModal as default,
  UserConnectionsTabs,
  type UserConnectionsModalProps,
};
